import { Link } from "react-router-dom";
import { routeMeta, usePageMeta } from "../../lib/usePageMeta";
import { COMPANY_SITE_URL } from "../../components/MarketingLayout";

/**
 * Who operates Odingard Studio. The founder block is deliberately empty of
 * invented biography: FOUNDER holds the facts the company has published, and a
 * photo, LinkedIn link and founder statement are added by the founder rather
 * than written on their behalf.
 */
const FOUNDER: { name: string; role: string; photoUrl: string | null; linkedInUrl: string | null; statement: string | null } = {
  name: "",
  role: "Founder",
  photoUrl: null,
  linkedInUrl: null,
  statement: null,
};

export function About() {
  usePageMeta(routeMeta("/about"));

  return (
    <>
      <section className="hero">
        <div className="container">
          <span className="hero__eyebrow">About</span>
          <h1>Who builds Odingard Studio.</h1>
          <p className="lede">
            Odingard Studio is operated by Odingard. We build governed AI applications for teams whose work is audited:
            government contractors, and the legal, compliance and risk functions that have to prove how an answer was
            reached.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>Why the product is built this way</h2>
            <p>
              Most AI tools are judged on whether the answer sounds right. In regulated work the answer has to be
              traceable: which document it came from, who verified it, and whether that document has changed since. So
              every application here fails closed rather than guessing, cites the source it relied on, keeps the record
              append only, and never lets one tenant read another. That is the whole design constraint, and it is the
              reason we did not build a chat box over your files.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>The team</h2>
          </div>
          {FOUNDER.name ? (
            <div className="feature" style={{ maxWidth: 640 }}>
              {FOUNDER.photoUrl && (
                <img
                  src={FOUNDER.photoUrl}
                  alt={`${FOUNDER.name}, ${FOUNDER.role} of Odingard`}
                  width={96}
                  height={96}
                  style={{ borderRadius: "50%" }}
                />
              )}
              <h3>{FOUNDER.name}</h3>
              <p>{FOUNDER.role}</p>
              {FOUNDER.statement && <p>{FOUNDER.statement}</p>}
              {FOUNDER.linkedInUrl && <a href={FOUNDER.linkedInUrl}>LinkedIn</a>}
            </div>
          ) : (
            <p>
              Odingard is a small, founder led team. The founder profile is published at{" "}
              <a href={COMPANY_SITE_URL}>odingard.com</a>. To speak to the person who builds this, use{" "}
              <Link to="/contact">contact</Link> and you will reach us directly, not a queue.
            </p>
          )}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <h2>Who this is not for</h2>
            <p>
              Solo developers, and teams with no regulated or audited work. If nobody ever asks you to prove how an
              answer was reached, a general purpose assistant is cheaper and will serve you better.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
